import httpService from "../../utils/httpService"
import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getAdminId} from "../../utils/common"
import {setToastr} from "../App/appSlice"

export interface User {
    appUserId: string
    firstName: string
    lastName: string
    emailAddress: string
    updatedByUserId: string | null
    updatedDate: string | null
    externalUserState: string | null
    appRole: string | null
    userActive: boolean
    accountEnabled: string
    appUserRoleId: string | null
}

export interface AppUser {
    appUserID: string
    firstName: string
    lastName: string
    emailAddress: string
    appUserRoleId: string | null
}

export interface Role {
    id: string
    description: string
    displayName: string
    isEnabled: boolean
    value: string
}

export interface UserDealer {
    regionID: string
    regionName: string
    dealerId: string
    dealerName: string
    dealerNumber: string
    assigned: boolean
    active: boolean
}

type UserState = {
    usersLoading: boolean
    isUserModalEnabled: boolean
    showEditUserModal: boolean
    users: User[]
    newUserData: User | null
    error: string
    inviteUser: User | null
    selectedUserRole: Role | null
    userRoleChangedAt: number
    isRoleLoading: boolean
    roles: Role[] | null
    isAppRolesLoading: false
    userDealers: UserDealer[] | null
    selectedUser: User
    selectedDealers: string[]
    userDetailsLoading: boolean
    userRoleLoading: boolean
    userDealersLoading: boolean
    isInviteModalLoading: boolean
    isUserInvited: boolean
    invitedUserError: string
    isAddEditModalToggled: boolean
    isAddEditModalLoading: boolean
    accountEnableLoader: boolean
    isUserExists: boolean
    isUserRoleExists: boolean
    appUsers: AppUser[]
    userDealerUpdateLoader: boolean
}

export const selectedUserInit = {
    appUserId: "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    updatedByUserId: "",
    updatedDate: "",
    externalUserState: "",
    appRole: "",
    userActive: false,
    accountEnabled: "",
    appUserRoleId: "",
}

const initialState: UserState = {
    usersLoading: false,
    isUserModalEnabled: false,
    showEditUserModal: false,
    users: [],
    newUserData: null,
    error: "",
    selectedUserRole: null,
    isRoleLoading: false,
    inviteUser: null,
    roles: null,
    isAppRolesLoading: false,
    userDealers: null,
    selectedUser: selectedUserInit,
    selectedDealers: [],
    userDetailsLoading: false,
    userRoleLoading: false,
    userDealersLoading: false,
    isInviteModalLoading: false,
    isUserInvited: false,
    invitedUserError: "",
    isAddEditModalToggled: false,
    isAddEditModalLoading: false,
    accountEnableLoader: false,
    userRoleChangedAt: 0,
    isUserExists: false,
    isUserRoleExists: false,
    appUsers: [],
    userDealerUpdateLoader: false,
}

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        showLoader(state, action: PayloadAction<boolean>) {
            state.usersLoading = action.payload
        },
        toggleAddUserModal(state, action: PayloadAction<boolean>) {
            state.isUserModalEnabled = action.payload
        },
        setShowEditUserModal(state, action: PayloadAction<boolean>) {
            state.showEditUserModal = action.payload
        },
        setUsers(state, action: PayloadAction<User[]>) {
            state.users = action.payload
        },
        setUserRole(state, action: PayloadAction<Role | null>) {
            state.selectedUserRole = action.payload
        },
        setUserDealers(state, action: PayloadAction<any>) {
            state.userDealers = action.payload
        },
        setSelectedUser(state, action: PayloadAction<User>) {
            state.selectedUser = action.payload
        },
        setAssignedDealers(state, action: PayloadAction<string[]>) {
            state.selectedDealers = action.payload
        },
        setRoles(state, action: PayloadAction<Role[]>) {
            state.roles = action.payload
        },
        setUserDetailsLoading(state, action: PayloadAction<boolean>) {
            state.userDetailsLoading = action.payload
        },
        setUserRoleLoading(state, action: PayloadAction<boolean>) {
            state.userRoleLoading = action.payload
        },
        setUserDealersLoading(state, action: PayloadAction<boolean>) {
            state.userDealersLoading = action.payload
        },
        toggleAddEditUserModal(state, action: PayloadAction<boolean>) {
            state.isAddEditModalToggled = action.payload
        },
        toggleFormLoad(state, action: PayloadAction<boolean>) {
            state.isAddEditModalLoading = action.payload
        },
        setIsUserExists(state, action: PayloadAction<boolean>) {
            state.isUserExists = action.payload
        },
        setIsUserRoleExists(state, action: PayloadAction<boolean>) {
            state.isUserRoleExists = action.payload
        },
        setAppUsers(state, action: PayloadAction<AppUser[]>) {
            state.appUsers = action.payload
        },
        setUserDealerUpdateLoading(state, action: PayloadAction<boolean>) {
            state.userDealerUpdateLoader = action.payload
        },
    },
})

export const {
    setUsers,
    setUserRole,
    setUserDetailsLoading,
    showLoader,
    toggleAddUserModal,
    setShowEditUserModal,
    setUserDealers,
    setSelectedUser,
    setRoles,
    setAssignedDealers,
    setUserRoleLoading,
    setUserDealersLoading,
    toggleAddEditUserModal,
    toggleFormLoad,
    setIsUserExists,
    setIsUserRoleExists,
    setAppUsers,
    setUserDealerUpdateLoading,
} = userSlice.actions

export default userSlice.reducer

// #region Thunks
export const fetchUsers = (): AppThunk => (dispatch: any, getState) => {
    dispatch(showLoader(true))
    httpService(getAccessToken(getState()))
        .get("users")
        .then(res => {
            dispatch(showLoader(false))
            const userDetail: User[] = res.data.map((item: any) => ({
                ...item,
                accountEnabled: item.accountEnabled ? "Active" : "Inactive",
            }))
            dispatch(setUsers(userDetail))
        })
        .catch(error => {
            dispatch(showLoader(false))
            dispatch(setUsers([]))
        })
}

export const fetchAppUsers = (): AppThunk => (dispatch: any, getState) => {
    httpService(getAccessToken(getState()))
        .get("users/appusers")
        .then(res => dispatch(setAppUsers(res.data)))
        .catch(error => dispatch(setAppUsers([])))
}

const showValidationError = (dispatch: any, message: string) =>
    dispatch(
        setToastr({
            isActive: true,
            message: message,
            delay: 100000,
            title: "Error",
            type: "danger",
            description: "",
        }),
    )

export const getUserRole =
    (userId: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setUserRoleLoading(true))
        httpService(getAccessToken(getState()))
            .get(`users/${userId}/appRole`)
            .then(res => {
                dispatch(setUserRole(res.data))
                //check a user role in usermodel with backend role.
                dispatch(checkUserRole(userId, res.data.id))
                dispatch(setUserRoleLoading(false))
            })
            .catch(error => {
                dispatch(setUserRole(null))
                dispatch(setUserRoleLoading(false))
            })
            .finally(() => {
                let selectedRole = getState().containers.users.selectedUserRole
                let activeToastrMessage = getState().application.toastr.message
                selectedRole === null &&
                    showValidationError(
                        dispatch,
                        activeToastrMessage === ""
                            ? "Role assignment pending"
                            : `${activeToastrMessage} along with a Role`,
                    )
            })
    }

export const getSelectedUserDetails =
    (userId: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setUserDetailsLoading(true))
        httpService(getAccessToken(getState()))
            .get(`users/${userId}`)
            .then(res => {
                dispatch(setSelectedUser(res.data))
                dispatch(setUserDetailsLoading(false))

                let message = ""

                if (res.data.firstName === null) message += "first"
                if (res.data.lastName === null) message += res.data.firstName === null ? " and " : "last"

                message !== "" && showValidationError(dispatch, `Please add user's ${message} name in active directory`)
            })
            .catch(error => {
                dispatch(setSelectedUser(selectedUserInit))
                dispatch(setUserDetailsLoading(false))
            })
    }

export const getUserDealers =
    (userId: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setUserDealersLoading(true))

        httpService(getAccessToken(getState()))
            .get(`users/${userId}/dealers`)
            .then(res => {
                dispatch(setUserDealers(res.data))
                dispatch(setAssignedDealers([]))
            })
            .catch(error => dispatch(setUserDealers([])))
            .finally(() => dispatch(setUserDealersLoading(false)))
    }

export const fetchAllRoles = (): AppThunk => (dispatch: any, getState) => {
    httpService(getAccessToken(getState()))
        .get(`users/appRoles`)
        .then(res => {
            const roles: Role[] | null = res.data
            if (roles) dispatch(setRoles(roles))
        })
        .catch(error => dispatch(setRoles([])))
}

export const postUserDealers =
    (userId: string): AppThunk =>
    (dispatch: any, getState) => {
        const userDealerParams = getState().containers.users.selectedDealers
        const adminId = getAdminId(getState())

        dispatch(setUserDealerUpdateLoading(true))
        httpService(getAccessToken(getState()))
            .post(`users/${userId}/dealers`, {
                adminId,
                userDealerParams,
                dealerIds: userDealerParams.join(", "),
            })
            .then(() => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Dealer assignments updated",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
            })
            .catch(error =>
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                ),
            )
            .finally(() => {
                dispatch(setUserDealerUpdateLoading(false))
                dispatch(toggleFormLoad(false))
                dispatch(toggleAddEditUserModal(false))
                dispatch(fetchUsers())
                dispatch(fetchAppUsers())
            })
    }

export const addEditUser = (): AppThunk => async (dispatch: any, getState) => {
    const selectedUser = getState().containers.users.selectedUser
    const userDealers = getState().containers.users.selectedDealers
    const selectedUserRole = getState().containers.users.selectedUserRole
    const userExists = getState().containers.users.isUserExists
    const userroleExists = getState().containers.users.isUserRoleExists
    const newUserDetails = {
        appUserID: selectedUser.appUserId,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        emailAddress: selectedUser.emailAddress,
        appUserRoleId: selectedUserRole === null ? "" : selectedUserRole.id,
    }

    if (userDealers.length === 0) showValidationError(dispatch, "Please select at least one dealer")
    else {
        dispatch(toggleFormLoad(true))
        dispatch(!userExists || !userroleExists ? addAppUser(newUserDetails) : postUserDealers(selectedUser.appUserId))
    }
    dispatch(toggleFormLoad(false))
}

export const checkUserExists =
    (appuserId: string): AppThunk =>
    (dispatch: any, getState) => {
        httpService(getAccessToken(getState()))
            .get(`users/appuser/${appuserId}/check`)
            .then((res: {data: boolean}) => {
                dispatch(setIsUserExists(res.data))
                dispatch(getUserDealers(res.data ? appuserId : "NONE"))
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Couldn't check if user exists in system",
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
    }

export const checkUserRole =
    (appuserId: string, approleId: string | undefined): AppThunk =>
    (dispatch: any, getState) => {
        httpService(getAccessToken(getState()))
            .get(`users/appuser/${appuserId}/approle/${approleId}/check`)
            .then((res: {data: boolean}) => dispatch(setIsUserRoleExists(res.data)))
    }

export const addAppUser =
    (user: AppUser): AppThunk =>
    (dispatch: any, getState) => {
        httpService(getAccessToken(getState()))
            .post(`users/appuser`, user)
            .then(async res => {
                if (res.data.appUserID !== "") {
                    dispatch(postUserDealers(res.data.appUserID))
                }
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
                dispatch(toggleFormLoad(false))
            })
            .finally(() => {})
    }
